import { useState, useEffect, useCallback } from 'react';
import { Home, Save, Check, RefreshCw } from 'lucide-react';
import getApiUrl from '../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


const SELECT_OPTIONS = {
  type_bien: [
    { value: '', label: 'Non renseigne' },
    { value: 'maison', label: 'Maison' },
    { value: 'appartement', label: 'Appartement' }
  ],
  statut_occupation: [
    { value: '', label: 'Non renseigne' },
    { value: 'proprietaire', label: 'Proprietaire' },
    { value: 'locataire', label: 'Locataire' },
    { value: 'proprietaire_bailleur', label: 'Proprietaire bailleur' }
  ],
  residence_type: [
    { value: '', label: 'Non renseigne' },
    { value: 'principale', label: 'Principale' },
    { value: 'secondaire', label: 'Secondaire' }
  ],
  annee_construction: [
    { value: '', label: 'Non renseigne' },
    { value: 'moins_2_ans', label: 'Moins de 2 ans' },
    { value: '2_15_ans', label: '2 a 15 ans' },
    { value: 'plus_15_ans', label: 'Plus de 15 ans' }
  ],
  nb_niveaux: [
    { value: '', label: 'Non renseigne' },
    { value: 'plain_pied', label: 'Plain-pied' },
    { value: '1_etage', label: '1 etage' },
    { value: '2_plus', label: '2 etages ou plus' }
  ],
  chauffage_actuel: [
    { value: '', label: 'Non renseigne' },
    { value: 'gaz', label: 'Gaz' },
    { value: 'fioul', label: 'Fioul' },
    { value: 'electrique', label: 'Electrique' },
    { value: 'bois', label: 'Bois' },
    { value: 'pac', label: 'Pompe a chaleur' },
    { value: 'autre', label: 'Autre' }
  ],
  eau_chaude: [
    { value: '', label: 'Non renseigne' },
    { value: 'meme', label: 'Meme que chauffage' },
    { value: 'ballon_electrique', label: 'Ballon electrique' },
    { value: 'solaire', label: 'Solaire' },
    { value: 'autre', label: 'Autre' }
  ],
  oui_non_nsp: [
    { value: '', label: 'Non renseigne' },
    { value: 'oui', label: 'Oui' },
    { value: 'non', label: 'Non' },
    { value: 'nsp', label: 'Ne sait pas' }
  ],
  type_vitrage: [
    { value: '', label: 'Non renseigne' },
    { value: 'simple', label: 'Simple vitrage' },
    { value: 'double', label: 'Double vitrage' },
    { value: 'triple', label: 'Triple vitrage' }
  ]
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs text-slate-500 mb-1">{label}</label>
    {children}
  </div>
);

const SelectField = ({ label, value, onChange, options, testId }) => (
  <Field label={label}>
    <select
      data-testid={testId}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none appearance-none"
    >
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </Field>
);

const InputField = ({ label, value, onChange, type = 'text', placeholder = '', testId }) => (
  <Field label={label}>
    <input
      data-testid={testId}
      type={type}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
    />
  </Field>
);

export default function LogementInfoSection({ clientId, authUser }) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [source, setSource] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/pipeline/client/row_${clientId}/logement-info`);
      if (r.ok) {
        const d = await r.json();
        setData(d.logement_info || {});
        setSource(d.source || '');
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const update = (key, val) => {
    setData(prev => ({ ...prev, [key]: val }));
    setSaved(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await authFetch(`${API}/pipeline/client/row_${clientId}/logement-info`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, user_email: authUser?.email || '' })
      });
      if (r.ok) { setSaved(true); setSource('logement_info'); }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-4">
        <div className="flex items-center gap-2 text-slate-400">
          <RefreshCw className="w-4 h-4 animate-spin" /> Chargement...
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-slate-800/60 to-slate-900/60 border border-teal-500/20 rounded-xl p-4" data-testid="logement-info-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-teal-400 uppercase tracking-wider flex items-center gap-2">
          <Home className="w-4 h-4" />
          Informations Logement
        </h3>
        {source === 'native_fields_fallback' && (
          <span className="text-xs text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded">Pre-rempli (ancien)</span>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {/* Identité logement */}
        <SelectField label="Type de bien" value={data.type_bien} onChange={v => update('type_bien', v)} options={SELECT_OPTIONS.type_bien} testId="logement-type-bien" />
        <SelectField label="Statut occupation" value={data.statut_occupation} onChange={v => update('statut_occupation', v)} options={SELECT_OPTIONS.statut_occupation} testId="logement-statut" />
        <SelectField label="Residence" value={data.residence_type} onChange={v => update('residence_type', v)} options={SELECT_OPTIONS.residence_type} testId="logement-residence" />
        <SelectField label="Annee construction" value={data.annee_construction} onChange={v => update('annee_construction', v)} options={SELECT_OPTIONS.annee_construction} testId="logement-annee" />
        <InputField label="Surface (m2)" value={data.surface_m2} onChange={v => update('surface_m2', v)} type="number" placeholder="65" testId="logement-surface" />
        <SelectField label="Nb niveaux" value={data.nb_niveaux} onChange={v => update('nb_niveaux', v)} options={SELECT_OPTIONS.nb_niveaux} testId="logement-niveaux" />

        {/* Beneficiaire */}
        <InputField label="Age du beneficiaire" value={data.age_beneficiaire} onChange={v => update('age_beneficiaire', v)} type="number" placeholder="72" testId="logement-age-beneficiaire" />
        <InputField label="Nb personnes foyer fiscal" value={data.nb_personnes_foyer} onChange={v => update('nb_personnes_foyer', v)} type="number" placeholder="2" testId="logement-nb-foyer" />

        {/* Chauffage */}
        <SelectField label="Chauffage actuel" value={data.chauffage_actuel} onChange={v => update('chauffage_actuel', v)} options={SELECT_OPTIONS.chauffage_actuel} testId="logement-chauffage" />
        <SelectField label="Eau chaude" value={data.eau_chaude} onChange={v => update('eau_chaude', v)} options={SELECT_OPTIONS.eau_chaude} testId="logement-eau-chaude" />
        <InputField label="Zone climatique" value={data.zone_climatique} onChange={v => update('zone_climatique', v)} placeholder="H1, H2a..." testId="logement-zone" />

        {/* Isolation */}
        <SelectField label="Isolation combles" value={data.isolation_combles} onChange={v => update('isolation_combles', v)} options={SELECT_OPTIONS.oui_non_nsp} testId="logement-iso-combles" />
        {data.isolation_combles === 'non' && (
          <InputField label="Surface combles (m2)" value={data.isolation_combles_surface} onChange={v => update('isolation_combles_surface', v)} type="number" testId="logement-iso-combles-surface" />
        )}
        <SelectField label="Isolation murs" value={data.isolation_murs} onChange={v => update('isolation_murs', v)} options={SELECT_OPTIONS.oui_non_nsp} testId="logement-iso-murs" />
        {data.isolation_murs === 'non' && (
          <InputField label="Surface murs (m2)" value={data.isolation_murs_surface} onChange={v => update('isolation_murs_surface', v)} type="number" testId="logement-iso-murs-surface" />
        )}
        <SelectField label="Isolation plancher" value={data.isolation_plancher} onChange={v => update('isolation_plancher', v)} options={SELECT_OPTIONS.oui_non_nsp} testId="logement-iso-plancher" />
        {data.isolation_plancher === 'non' && (
          <InputField label="Surface plancher (m2)" value={data.isolation_plancher_surface} onChange={v => update('isolation_plancher_surface', v)} type="number" testId="logement-iso-plancher-surface" />
        )}
        <SelectField label="Type vitrage" value={data.type_vitrage} onChange={v => update('type_vitrage', v)} options={SELECT_OPTIONS.type_vitrage} testId="logement-vitrage" />
        <InputField label="Nb fenetres simple vitrage" value={data.nb_fenetres} onChange={v => update('nb_fenetres', v)} type="number" testId="logement-nb-fenetres" />
        <SelectField label="VMC existante" value={data.vmc_existante} onChange={v => update('vmc_existante', v)} options={SELECT_OPTIONS.oui_non_nsp} testId="logement-vmc" />
      </div>

      {/* Save button */}
      <div className="flex items-center gap-3 mt-4">
        <button
          onClick={save}
          disabled={saving}
          className="px-6 py-2 bg-teal-600 hover:bg-teal-500 disabled:bg-slate-600 text-white rounded-lg font-medium flex items-center gap-2 transition-colors"
          data-testid="logement-save-btn"
        >
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
        {saved && (
          <span className="text-teal-400 text-sm flex items-center gap-1">
            <Check className="w-4 h-4" /> Sauvegarde !
          </span>
        )}
      </div>
    </div>
  );
}
